import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box } from '@mui/material';

import { Container, Loading, SimpleButton, ErrorMessage, SimpleTextField } from '@/src/ui';
import { type Option } from '@/src/utils';
import { apiRequest } from '@/src/services';
import QuizzesTable from './components/QuizzesTable';
import ShareQuizModal from './components/ShareQuizModal';
import PublishQuizModal from './components/PublishQuizModal';
import type { QuizListItemDTO } from './models/quizDTOs';

interface FilterDataResponse {
    gradeLevelMasterOptions: Option[];
}

const YourQuizzesPage: React.FC = () => {
    document.title = 'Your Quizzes';

    const navigate = useNavigate();

    const [loaded, setLoaded] = useState(false);
    const [error, setError] = useState('');
    const [searchString, setSearchString] = useState('');
    const [quizzes, setQuizzes] = useState<QuizListItemDTO[]>([]);
    const [gradeOptions, setGradeOptions] = useState<Option[]>([]);

    const [shareQuiz, setShareQuiz] = useState<QuizListItemDTO | null>(null);
    const [publishQuiz, setPublishQuiz] = useState<QuizListItemDTO | null>(null);

    const refreshQuizzes = useCallback(async () => {
        setError('');
        setLoaded(false);
        try {
            const res = await apiRequest<QuizListItemDTO[]>('GET', 'assessments/exams/quizzes');
            setQuizzes(res.data ?? []);
        } catch {
            setError('Failed to fetch quizzes');
        } finally {
            setLoaded(true);
        }
    }, []);

    useEffect(() => {
        (async () => {
            try {
                const res = await apiRequest<FilterDataResponse>(
                    'GET',
                    'utility/filterData?getGradeLevelMaster=true'
                );
                setGradeOptions(res.data.gradeLevelMasterOptions ?? []);
            } catch {
                // Grade names are best-effort; fall back to ids in the table.
            }
        })();
        refreshQuizzes();
    }, [refreshQuizzes]);

    const handleShareClose = useCallback(() => {
        setShareQuiz(null);
    }, []);

    const handlePublishModalClose = useCallback(
        (shouldRefresh: boolean, isInTimeout: boolean) => {
            if (!isInTimeout) {
                setPublishQuiz(null);
            }
            if (shouldRefresh) {
                refreshQuizzes();
            }
        },
        [refreshQuizzes]
    );

    return (
        <Container>
            <h1 className="page-header">Your Quizzes</h1>
            <br />

            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 2,
                }}
            >
                <SimpleTextField
                    name="searchQuizzes"
                    label="Search by title..."
                    variant="outlined"
                    value={searchString}
                    onChange={(e) => setSearchString(e.target.value)}
                    sx={{ minWidth: 170, maxWidth: 300, mr: 2 }}
                />

                <SimpleButton
                    onClick={() => navigate('/exams/dynamicQuizGenerator')}
                    variant="contained"
                    color="primary"
                    className="whitespace-nowrap"
                >
                    Add Quiz
                </SimpleButton>
            </Box>

            <Loading visible={!loaded} message="Loading quizzes..." />
            <ErrorMessage error={error} style={{ textAlign: 'center' }} />

            {loaded && (
                <QuizzesTable
                    data={quizzes}
                    gradeOptions={gradeOptions}
                    searchString={searchString}
                    onShare={setShareQuiz}
                    onPublishToggle={setPublishQuiz}
                />
            )}

            {shareQuiz && (
                <ShareQuizModal open={!!shareQuiz} quiz={shareQuiz} onClose={handleShareClose} />
            )}

            {publishQuiz && (
                <PublishQuizModal quiz={publishQuiz} handleClose={handlePublishModalClose} />
            )}
        </Container>
    );
};

export default YourQuizzesPage;
